import useGetMySelf from "@/features/auth/use-get-my-self";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import {
  PathPickMembership,
  PathPickMembershipStatus,
} from "@/features/types";

/**
 * 현재 회사의 패스픽 멤버십 권한.
 * 기능 게이트는 반드시 allow*·quota 필드로 판단한다 (code는 표시용).
 * 멤버십이 없으면(null/로딩/에러) 모든 권한은 false(기본 거부).
 */
const useMembership = () => {
  const { data, isLoading } = useGetMySelf();
  const { companyId } = useCompanyIdentity();

  const currentCompany = data?.companies.find(
    (company) => company.id === companyId
  );
  const membership: PathPickMembership | undefined =
    currentCompany?.pathpickMembership ?? undefined;

  // 멤버십 상태 — GNB 노출·멤버십 화면 분기용.
  // 구버전 백엔드 응답(필드 없음)에서는 배정 여부만으로 근사한다.
  const membershipStatus: PathPickMembershipStatus =
    currentCompany?.pathpickMembershipStatus ??
    (membership !== undefined ? "SUBSCRIBED" : "NONE");

  // 체험·구독 만료 — 페이지 진입은 되지만 내부 기능은 잠긴다.
  const isMembershipExpired =
    membershipStatus === "TRIAL_EXPIRED" ||
    membershipStatus === "SUBSCRIPTION_EXPIRED";

  return {
    membership,
    isMembershipLoading: isLoading,
    membershipStatus,
    isMembershipExpired,
    // 만료 안내 패널에 넘길 좁힌 상태 — 캐스팅 없이 status prop에 그대로 꽂으라고 분리해 둔다.
    expiredStatus: isMembershipExpired
      ? (membershipStatus as "TRIAL_EXPIRED" | "SUBSCRIPTION_EXPIRED")
      : null,
    // 무료체험 남은 시간 표시용 만료 시각(ISO). 유료 플랜은 null(무기한).
    membershipExpiresAt: membership?.expiresAt ?? null,
    // 무료체험 소진 여부 — 바이어당 1회라 true면 체험 CTA를 감춘다.
    hasUsedFreeTrial: currentCompany?.hasUsedPathpickFreeTrial ?? false,
    // 멤버십 미보유(null)면 패스픽 접근 권한 자체가 없다 — GNB 노출 게이트
    hasPathPickMembership: membership !== undefined,
    // 사상 첫 결제 여부 — 첫 달 50% 프로모션 대상.
    // 멤버십 미보유만으로는 부족하다(해지·반려 후 재신청도 미보유 상태다). 신청 이력 필드까지 함께 본다.
    // 로딩 중에는 false — 기존 회원에게 프로모션 UI가 깜빡였다 사라지는 것을 막는다.
    isFirstPathPickPayment:
      !isLoading &&
      membership === undefined &&
      !(currentCompany?.hasPathpickMembershipApplication ?? false),
    // 요청 방식: API 연동 허용 여부.
    // API 연동은 BASIC까지 제공으로 정책이 바뀌었는데, BE가 아직 BASIC에 allowNaverSync=false를
    // 내려주므로 그동안만 code로 보정한다.
    // TODO(BE): BASIC allowNaverSync=true 반영되면 아래 code 분기를 지운다.
    isApiSync:
      (membership?.allowNaverSync ?? false) || membership?.code === "BASIC",
    // 다운로드 모드 허용: 네이버+크림 다운로드 모두 true여야 함
    canDownload:
      (membership?.allowNaverDownload ?? false) &&
      (membership?.allowKreamDownload ?? false),
    // monthlyQuota === null은 무제한
    hasQuotaLimit: membership ? membership.monthlyQuota !== null : true,
    // i18n 표시용 소문자 키: membership:plan.${planKey}.name
    planKey: membership?.code.toLowerCase(),
    showHotDeal: membership?.allowHotdeal ?? false,
    allowStockOrder: membership?.allowStockOrder ?? false,
    stockOrderMonthlyLimit: membership?.stockOrderMonthlyLimit ?? 0,
  };
};

export default useMembership;
